// End-to-end process maps for the process guide (docs/design/session-standards.md).
// Each process is an ordered list of stages; a stage names the screens
// (session codes from navigation.ts) where the user does that part of the
// work. The guide highlights the stage of the screen the user is on, so a
// user who clicked through menus always sees where they are, what came
// before and what comes next — the SAP "document flow" / D365 "business
// process" idea, on the level of screens instead of single documents.
//
// The same data drives the one-page process map (Home → Process map,
// PX-SYS-230): processes are lanes, stages are cards, processLinks are the
// hand-offs between lanes, and stages marked "posts" write to the inventory
// ledger. Keep it in step with docs/process-map.md.
//
// Pure data: texts live in i18n under guide.processes.<process> (stage
// descriptions) and processMap (short stage titles, link labels).

import type { ModuleTone } from "@/navigation";

export const processIds = [
  "procureToStock",
  "orderToDelivery",
  "planToProduce",
  "inventoryControl",
  "quality",
  "project",
  "setup",
] as const;
export type ProcessId = (typeof processIds)[number];

export interface ProcessStage {
  key: string;
  /** Screens of this stage (session codes), first one is the main screen. */
  sessions: readonly string[];
  /** The stage posts to the inventory ledger (receipt, issue, transfer, count…). */
  posts?: boolean;
  /**
   * Column on the process map (0–6, increasing along the lane): stages that
   * hand off to each other sit in the same or a neighbouring column, so the
   * arrows between lanes run mostly vertically. 0 = master data / set-up,
   * 1 = demand, 2 = plan, 3 = order, 4 = execute, 5 = check, 6 = result.
   */
  col: number;
  /** Work waiting at this step: a key of process_work_counts and the list filter that shows it. */
  work?: StageWork;
}

export const workKeys = [
  "po_draft",
  "po_to_receive",
  "so_draft",
  "so_to_ship",
  "planned_open",
  "mo_planned",
  "mo_active",
  "inspections_open",
  "ncr_open",
  "moves_draft",
  "counts_open",
  "projects_active",
] as const;
export type WorkKey = (typeof workKeys)[number];

export interface StageWork {
  key: WorkKey;
  /** Screen that lists the waiting documents (defaults to the stage's main screen). */
  session?: string;
  /** Search params for that screen (status filter). */
  search?: Readonly<Record<string, string>>;
}

export const MAP_COLUMNS = 7;

export const processes: Record<ProcessId, readonly ProcessStage[]> = {
  procureToStock: [
    { key: "masterData", sessions: ["PX-PUR-310", "PX-PUR-320", "PX-MDT-110"], col: 0 },
    { key: "order", sessions: ["PX-PUR-110"], col: 3, work: { key: "po_draft", search: { status: "draft" } } },
    { key: "receive", sessions: ["PX-PUR-210", "PX-PUR-220"], posts: true, col: 4, work: { key: "po_to_receive" } },
    { key: "inspect", sessions: ["PX-QMS-110"], col: 5, work: { key: "inspections_open", search: { status: "open" } } },
    { key: "stock", sessions: ["PX-WHS-110"], col: 6 },
    { key: "returnToSupplier", sessions: ["PX-PUR-120"], posts: true, col: 6 },
  ],
  orderToDelivery: [
    { key: "masterData", sessions: ["PX-SAL-310", "PX-SAL-320", "PX-SAL-330", "PX-MDT-110"], col: 0 },
    { key: "order", sessions: ["PX-SAL-110"], col: 1, work: { key: "so_draft", search: { status: "draft" } } },
    { key: "pick", sessions: ["PX-SAL-210"], col: 4, work: { key: "so_to_ship" } },
    { key: "ship", sessions: ["PX-SAL-220"], posts: true, col: 5 },
    { key: "return", sessions: ["PX-SAL-120"], posts: true, col: 6 },
  ],
  planToProduce: [
    { key: "engineering", sessions: ["PX-ENG-310", "PX-ENG-110", "PX-ENG-120", "PX-ENG-130", "PX-MFG-220", "PX-MFG-310"], col: 0 },
    { key: "mrp", sessions: ["PX-PLN-110", "PX-PLN-210"], col: 1 },
    { key: "plannedOrders", sessions: ["PX-PLN-120", "PX-PLN-130"], col: 2, work: { key: "planned_open", search: { status: "open" } } },
    { key: "schedule", sessions: ["PX-PLN-140", "PX-MFG-120"], col: 3, work: { key: "mo_planned", session: "PX-MFG-110", search: { status: "planned" } } },
    { key: "produce", sessions: ["PX-MFG-110", "PX-MFG-130"], posts: true, col: 4, work: { key: "mo_active" } },
    { key: "stock", sessions: ["PX-WHS-110"], col: 6 },
  ],
  inventoryControl: [
    { key: "structure", sessions: ["PX-WHS-310", "PX-WHS-320", "PX-WHS-330"], col: 0 },
    { key: "move", sessions: ["PX-WHS-220", "PX-WHS-210"], posts: true, col: 4, work: { key: "moves_draft", search: { status: "draft" } } },
    { key: "count", sessions: ["PX-WHS-230"], posts: true, col: 5, work: { key: "counts_open" } },
    { key: "stock", sessions: ["PX-WHS-110", "PX-WHS-120", "PX-MDT-130", "PX-MDT-140"], col: 6 },
  ],
  quality: [
    { key: "plans", sessions: ["PX-QMS-210", "PX-QMS-220"], col: 0 },
    { key: "inspect", sessions: ["PX-QMS-110", "PX-WHS-110"], posts: true, col: 5, work: { key: "inspections_open", search: { status: "open" } } },
    { key: "nonconformance", sessions: ["PX-QMS-120"], col: 6, work: { key: "ncr_open", search: { status: "open" } } },
  ],
  project: [
    { key: "setup", sessions: ["PX-PRJ-110"], col: 0 },
    { key: "peg", sessions: ["PX-SAL-110", "PX-PUR-110", "PX-MFG-110"], col: 1 },
    { key: "time", sessions: ["PX-PRJ-210"], col: 4 },
    { key: "progress", sessions: ["PX-PRJ-120"], col: 6, work: { key: "projects_active" } },
  ],
  setup: [
    { key: "organization", sessions: ["PX-SYS-110", "PX-SYS-120"], col: 0 },
    { key: "numbering", sessions: ["PX-SYS-210"], col: 1 },
    { key: "units", sessions: ["PX-MDT-120"], col: 2 },
    { key: "items", sessions: ["PX-MDT-110", "PX-MDT-210"], col: 3 },
    { key: "audit", sessions: ["PX-SYS-220"], col: 6 },
  ],
};

/** Module colour of each lane on the process map. */
export const processTones: Record<ProcessId, ModuleTone> = {
  procureToStock: "procurement",
  orderToDelivery: "sales",
  planToProduce: "planning",
  inventoryControl: "warehousing",
  quality: "quality",
  project: "project",
  setup: "settings",
};

export interface ProcessLink {
  /** i18n key under processMap.links. */
  key: string;
  from: { process: ProcessId; stage: string };
  to: { process: ProcessId; stage: string };
  /** Automatic link (reservation, MRP pegging, generated document) vs. a document the user creates. */
  automatic?: boolean;
}

/** Hand-offs between processes: the document of one lane that starts work in another. */
export const processLinks: readonly ProcessLink[] = [
  { key: "salesDemand", from: { process: "orderToDelivery", stage: "order" }, to: { process: "planToProduce", stage: "mrp" }, automatic: true },
  { key: "plannedPurchase", from: { process: "planToProduce", stage: "plannedOrders" }, to: { process: "procureToStock", stage: "order" } },
  { key: "plannedSubcontract", from: { process: "planToProduce", stage: "plannedOrders" }, to: { process: "procureToStock", stage: "order" } },
  { key: "planStale", from: { process: "planToProduce", stage: "schedule" }, to: { process: "planToProduce", stage: "mrp" }, automatic: true },
  { key: "receiptInspection", from: { process: "procureToStock", stage: "receive" }, to: { process: "quality", stage: "inspect" }, automatic: true },
  { key: "reportInspection", from: { process: "planToProduce", stage: "produce" }, to: { process: "quality", stage: "inspect" }, automatic: true },
  { key: "returnInspection", from: { process: "orderToDelivery", stage: "return" }, to: { process: "quality", stage: "inspect" }, automatic: true },
  { key: "disposition", from: { process: "quality", stage: "nonconformance" }, to: { process: "inventoryControl", stage: "move" } },
  { key: "ncrReturn", from: { process: "quality", stage: "nonconformance" }, to: { process: "procureToStock", stage: "returnToSupplier" }, automatic: true },
  { key: "pegSales", from: { process: "project", stage: "peg" }, to: { process: "orderToDelivery", stage: "order" }, automatic: true },
  { key: "pegPurchase", from: { process: "project", stage: "peg" }, to: { process: "procureToStock", stage: "order" }, automatic: true },
  { key: "pegProduction", from: { process: "project", stage: "peg" }, to: { process: "planToProduce", stage: "produce" }, automatic: true },
  { key: "subcontractOrder", from: { process: "planToProduce", stage: "produce" }, to: { process: "procureToStock", stage: "order" }, automatic: true },
  { key: "subcontractReceipt", from: { process: "procureToStock", stage: "receive" }, to: { process: "planToProduce", stage: "produce" }, automatic: true },
  { key: "subcontractMaterial", from: { process: "planToProduce", stage: "produce" }, to: { process: "inventoryControl", stage: "move" } },
  { key: "subcontractItemMaterial", from: { process: "procureToStock", stage: "order" }, to: { process: "inventoryControl", stage: "move" } },
];

// Scenarios: one business case walked step by step across the lanes (the
// "Scenarios" view and the player on the map). Texts: processMap.scenarios.<id>.
export const scenarioIds = ["makeToOrder", "buyToStock", "qualityReject", "projectDelivery", "inventoryCheck", "companySetup"] as const;
export type ScenarioId = (typeof scenarioIds)[number];

export const scenarios: Record<ScenarioId, readonly { process: ProcessId; stage: string }[]> = {
  makeToOrder: [
    { process: "orderToDelivery", stage: "order" },
    { process: "planToProduce", stage: "mrp" },
    { process: "planToProduce", stage: "plannedOrders" },
    { process: "planToProduce", stage: "schedule" },
    { process: "planToProduce", stage: "produce" },
    { process: "quality", stage: "inspect" },
    { process: "orderToDelivery", stage: "pick" },
    { process: "orderToDelivery", stage: "ship" },
  ],
  buyToStock: [
    { process: "planToProduce", stage: "mrp" },
    { process: "planToProduce", stage: "plannedOrders" },
    { process: "procureToStock", stage: "order" },
    { process: "procureToStock", stage: "receive" },
    { process: "procureToStock", stage: "inspect" },
    { process: "procureToStock", stage: "stock" },
  ],
  qualityReject: [
    { process: "procureToStock", stage: "receive" },
    { process: "quality", stage: "inspect" },
    { process: "quality", stage: "nonconformance" },
    { process: "inventoryControl", stage: "move" },
  ],
  projectDelivery: [
    { process: "project", stage: "setup" },
    { process: "project", stage: "peg" },
    { process: "orderToDelivery", stage: "order" },
    { process: "planToProduce", stage: "produce" },
    { process: "project", stage: "time" },
    { process: "project", stage: "progress" },
  ],
  inventoryCheck: [
    { process: "inventoryControl", stage: "structure" },
    { process: "inventoryControl", stage: "move" },
    { process: "inventoryControl", stage: "count" },
    { process: "inventoryControl", stage: "stock" },
  ],
  companySetup: [
    { process: "setup", stage: "organization" },
    { process: "setup", stage: "numbering" },
    { process: "setup", stage: "units" },
    { process: "setup", stage: "items" },
    { process: "inventoryControl", stage: "structure" },
    { process: "quality", stage: "plans" },
  ],
};

/** The hand-off between two consecutive scenario steps, if the map has one. */
export function linkBetween(a: { process: ProcessId; stage: string }, b: { process: ProcessId; stage: string }): ProcessLink | null {
  return (
    processLinks.find(
      (l) => l.from.process === a.process && l.from.stage === a.stage && l.to.process === b.process && l.to.stage === b.stage,
    ) ?? null
  );
}

/** Every distinct stage key, for the short-title texts (processMap.stages). */
export const stageKeys: readonly string[] = [...new Set(processIds.flatMap((id) => processes[id].map((s) => s.key)))];

/** Processes that contain a screen, with the index of its stage, in config order. */
export function processesOf(session: string | null): { id: ProcessId; stage: number }[] {
  if (!session) return [];
  const out: { id: ProcessId; stage: number }[] = [];
  for (const id of processIds) {
    const stage = processes[id].findIndex((s) => s.sessions.includes(session));
    if (stage >= 0) out.push({ id, stage });
  }
  return out;
}
